import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  captureClientPageview,
  getAnalyticsDistinctId,
  identifyClientAnalytics,
  initializeClientAnalytics,
  resetClientAnalytics,
  setClientAnalyticsProject,
} from '../lib/clientAnalytics';

/**
 * Privacy-safe ChannelPlex product analytics. This component must stay inside
 * BrowserRouter but outside PrivateRoute so it can reset identity after every
 * logout and account switch, including session-expiry logout.
 */
export default function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const distinctId = useMemo(() => getAnalyticsDistinctId(user), [user?.userId, user?.userCode]);
  const identityOwner = useRef<string | null | undefined>(undefined);
  const lastPageviewPath = useRef<string | null>(null);

  useEffect(() => {
    void initializeClientAnalytics();
  }, []);

  useEffect(() => {
    if (distinctId) {
      identifyClientAnalytics(distinctId, {
        admin_role: user?.adminRole || 'none',
        user_type: user?.isExternalUser ? 'external' : 'employee',
      });
      setClientAnalyticsProject(selectedProjectId);
      identityOwner.current = distinctId;
      return;
    }

    // The undefined sentinel also resets a PostHog identity persisted by an
    // earlier browser session before the anonymous login page can be captured.
    if (identityOwner.current !== null) {
      resetClientAnalytics();
      identityOwner.current = null;
    }
    setClientAnalyticsProject(null);
  }, [distinctId, selectedProjectId, user?.adminRole, user?.isExternalUser]);

  useEffect(() => {
    // Query and hash changes are deliberately ignored. The raw pathname is
    // only used as a navigation signal; the bridge sends a normalized template.
    if (!location.pathname || lastPageviewPath.current === location.pathname) return;
    lastPageviewPath.current = location.pathname;
    captureClientPageview(location.pathname);
  }, [location.pathname]);

  return <>{children}</>;
}
